// Datenzugriff für den PIPR-Chart.
//
// Drei Quellen, gewählt über REACT_APP_PIPR_SOURCE:
//
//   firestore (Default)  pipr/{bank} + pipr/{bank}/years/*, über src/firebase.ts
//   files                statische Dateien unter REACT_APP_PIPR_DATA_BASE/{bank}
//   sample               lokale Beispieldaten
//
// Default ist Firestore, weil die Firebase-App ohnehin für das Newsletter-
// Formular initialisiert wird — eine gesetzte Variable weniger im Deploy.
// `sample` bleibt für die lokale Arbeit am Chart ohne Netz.
//
// Der Client rät in keiner Variante eine URL oder eine Dokument-ID: im
// Datei-Fall stehen alle Pfade im Manifest, im Firestore-Fall alle Jahre in
// `head.years`.
//
// Zwischen den Banken wird nichts geteilt: eigenes Delta-Raster, eigene
// Meeting-Liste, eigener Historienanfang. Ein Bankwechsel verwirft den
// geladenen Stand und liest den anderen Head.
//
// Offen für später: aktuell werden alle Historien-Jahre auf einmal geladen.
// Für Firestore ist der Hebel schon da — `loadFirestore(bank, false)` liefert
// nur den Head, also ein Read und das laufende Jahr; der Rest kann beim Wechsel
// der Zoomstufe nachgeholt werden.

import { useEffect, useState } from "react";
import { buildSampleBundle } from "./sampleData";
import { loadFirestore } from "./firestore";
import { toBundle } from "./wire";
import type { WireForward, WireHistory, WireManifest, WireMeeting } from "./wire";
import { SUPPORTED_SCHEMA } from "../types";
import type { Bank, PiprBundle } from "../types";

const SOURCE = (process.env.REACT_APP_PIPR_SOURCE || "firestore").toLowerCase();
const BASE = (process.env.REACT_APP_PIPR_DATA_BASE || "").replace(/\/$/, "");

async function getJson<T>(url: string): Promise<T> {
    const r = await fetch(url, { credentials: "omit" });
    if (!r.ok) throw new Error(r.status + " " + url);
    return (await r.json()) as T;
}

async function loadFiles(base: string, bank: Bank): Promise<PiprBundle> {
    const root = base + "/" + bank;
    const man = await getJson<WireManifest>(root + "/manifest.json");
    if (man.schema_version !== SUPPORTED_SCHEMA) throw new Error("schema");

    const [meetings, forward, ...years] = await Promise.all([
        getJson<WireMeeting[]>(root + "/" + man.meetings),
        getJson<WireForward>(root + "/" + man.forward),
        ...man.history.map((h) => getJson<WireHistory>(root + "/" + h.file)),
    ]);
    return toBundle(bank, man, meetings, forward, years as WireHistory[]);
}

export interface PiprDataState {
    data: PiprBundle | null;
    loading: boolean;
    /** "schema" verlangt ein Neuladen, "network" einen erneuten Versuch */
    error: "schema" | "network" | null;
}

export function usePiprData(bank: Bank): PiprDataState {
    const [state, setState] = useState<PiprDataState>({
        data: null,
        loading: true,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;

        if (SOURCE === "sample") {
            setState({ data: buildSampleBundle(bank), loading: false, error: null });
            return;
        }

        setState({ data: null, loading: true, error: null });

        const pending =
            SOURCE === "files" ? loadFiles(BASE, bank) : loadFirestore(bank);

        pending
            .then((data) => {
                if (!cancelled) setState({ data, loading: false, error: null });
            })
            .catch((err) => {
                if (cancelled) return;
                setState({
                    data: null,
                    loading: false,
                    error: err && err.message === "schema" ? "schema" : "network",
                });
            });

        return () => {
            cancelled = true;
        };
    }, [bank]);

    return state;
}